import React from 'react';
import {
  Box, FormHelperText, IconButton, Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import { WheelStore } from '@gamiphy/loyalty-station-desktop/store/campaignUsers/wheel';
import { Trans, useTranslation } from '@gamiphy/react-i18n';
import { GButton } from '@gamiphy/loyalty-station-desktop/uikit';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { useSpinTheWheelApi } from '@gamiphy/loyalty-station-desktop/hooks/apis';
import { WheelOptionDto } from '@coretava/api/events-service';
import { isAxiosError } from 'axios';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import Close from '@mui/icons-material/Close';
import { useMobile } from '@gamiphy/loyalty-station-desktop/utils/mobile';
import { DoNotShowAgain } from '@gamiphy/loyalty-station-desktop/components/cashback/cashbackPopup/components';
import { CashbackPopupsStore } from '@gamiphy/loyalty-station-desktop/store/cashbackPopups';
import { Wheel } from '../../../campaigns/wheel';

const Spin: React.FC<{
  onDone: () => void,
  openSignup: () => void
}> = ({
  onDone,
  openSignup,
}) => {
  const [resultOption, setResultOption] = React.useState<WheelOptionDto>();
  const spinTheWheelApi = useSpinTheWheelApi();
  const [error, setError] = React.useState<string>();
  const { t } = useTranslation();
  const isMobile = useMobile();

  const handleSpin = React.useCallback(async () => {
    if (AppStore.app && WheelStore.wheel) {
      try {
        const { data } = await spinTheWheelApi.wheelSpinsControllerCreate(AppStore.app.id, { wheelId: WheelStore.wheel.id, userId: UserStore.user?.id });
        setResultOption(data.wheelSpin.option);
      } catch (e) {
        if (isAxiosError(e)) {
          switch (e.response?.status) {
            case 409:
              setError(t('campaigns.boosters.wheel.alreadySpun', "You've already spun the wheel! Thanks for participating!"));
              break;
            default:
              throw e;
          }
        }
      }
    }
  }, [AppStore.app, WheelStore.wheel, UserStore.user]);

  const handleClick = React.useCallback(() => {
    if (UserStore.user?.phone) {
      void handleSpin();
    } else {
      openSignup();
    }
  }, [handleSpin, UserStore.user]);

  if (!WheelStore.wheel) return null;

  return (
    <Box>
      <Box p={2} display="flex" justifyContent="flex-end">
        <IconButton sx={{ ml: 'auto', p: 0 }} disableRipple onClick={() => CashbackPopupsStore.closeCashbackPopup()}>
          <Close fontSize="large" />
        </IconButton>
      </Box>
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 3 : 6} px={isMobile ? 1 : 6}>
        <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
          <Wheel wheel={WheelStore.wheel} resultOption={resultOption} afterRotation={onDone} size={isMobile ? 250 : 300} />
          {error && (
          <FormHelperText error>
            {error}
          </FormHelperText>
          )}
        </Box>
        <Box
          gap={2}
          pb={isMobile ? 0 : 5}
          pt={isMobile ? 0 : 4}
          maxWidth={isMobile ? 'unset' : 'min-content'}
          textAlign={isMobile ? 'center' : 'left'}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography fontSize={isMobile ? '3.2rem' : '4.8rem'} fontWeight={700} color="primary" textTransform="uppercase" whiteSpace="nowrap" lineHeight={1}>
              <Trans
                i18nKey="campaigns.boosters.spinTheWheel.title"
                defaults="Gire a Roleta"
              />
            </Typography>
            <Typography
              fontSize={isMobile ? '1.8rem' : '2.4rem'}
              fontWeight={500}
              sx={{
                ...isMobile && {
                  maxWidth: '60%',
                  mt: 2,
                },
              }}
            >
              <Trans
                i18nKey="campaigns.boosters.wheel.getPrize"
                defaults="Get your chance to win a prize!"
              />
            </Typography>
          </Box>
          <GButton variant="contained" fullWidth sx={{ py: '1.6rem', fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 500 }} onClick={handleClick}>
            <Trans
              i18nKey="campaigns.boosters.wheel.widget.try"
              defaults="Try your luck!"
            />
          </GButton>
        </Box>
      </Box>
      <Box p={2}>
        <DoNotShowAgain />
      </Box>
    </Box>
  );
};

export default observer(Spin);
